import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Collapse,
  Card,
  CardContent,
  Pagination,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Refresh,
  ExpandMore,
  ExpandLess,
  Person,
  Message,
} from '@mui/icons-material';
import axios from 'axios';

interface UserChatStats {
  user_id: string;
  username: string;
  email: string;
  session_count: number;
  total_messages: number;
  last_chat_time: string | null;
  created_at: string;
}

interface ChatSession {
  session_id: string;
  session_name: string;
  created_at: string | null;
  updated_at: string | null;
  message_count: number;
  first_message: {
    message: string;
    timestamp: string;
    sender: string;
  } | null;
  last_message: {
    message: string;
    timestamp: string;
    sender: string;
  } | null;
  recent_messages: Array<{
    id: number;
    message: string;
    sender: string;
    timestamp: string;
  }>;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://core.gamura.ai";

const UserChats: React.FC = () => {
  const [users, setUsers] = useState<UserChatStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userChats, setUserChats] = useState<ChatSession[]>([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchUsers = async (pageNum: number = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('gamora_token');
      const response = await axios.get(
        `${API_BASE_URL}/admin/chats?page=${pageNum}&limit=20`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setUsers(response.data.users);
      setTotalPages(response.data.pagination.pages);
      setPage(pageNum);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.detail || 'Failed to fetch users with chats');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserChats = async (userId: string) => {
    try {
      setLoadingChats(true);
      setError(null);
      
      const token = localStorage.getItem('gamora_token');
      const response = await axios.get(
        `${API_BASE_URL}/admin/users/${userId}/chats?page=1&limit=50`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setUserChats(response.data.sessions);
    } catch (err: any) {
      console.error('Error fetching user chats:', err);
      setError(err.response?.data?.detail || 'Failed to fetch user chats');
    } finally {
      setLoadingChats(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleExpandUser = (userId: string) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      setUserChats([]);
    } else {
      setExpandedUser(userId);
      fetchUserChats(userId);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} min ago`;
      if (diffHours < 24) return `${diffHours} hr ago`;
      if (diffDays < 7) return `${diffDays} days ago`;
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const filteredUsers = users.filter(
    user =>
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress size={24} sx={{ color: '#9ca3af' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography
          component="h1"
          sx={{ fontSize: '1.1rem', fontWeight: 650, color: '#111827', letterSpacing: '-0.02em' }}
        >
          User Chats
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => fetchUsers(page)}
          sx={{
            borderColor: '#e5e7eb',
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.78rem',
            color: '#6b7280',
            '&:hover': {
              borderColor: '#e5e7eb',
              bgcolor: '#f3f4f6',
            },
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Search by username or email..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            bgcolor: '#f8f9fb',
            fontSize: '0.85rem',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#111827',
            },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Person />
            </InputAdornment>
          ),
        }}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Users Table */}
      <TableContainer
        sx={{
          bgcolor: '#fff',
          border: '1px solid #eceef1',
          borderRadius: '12px',
          boxShadow: 'none',
          overflow: 'hidden',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8f9fb' }}>
              <TableCell sx={{ color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                User
              </TableCell>
              <TableCell align="right" sx={{ color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Sessions
              </TableCell>
              <TableCell align="right" sx={{ color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Messages
              </TableCell>
              <TableCell sx={{ color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Last Chat
              </TableCell>
              <TableCell sx={{ color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>
                  <Typography variant="body2" sx={{ py: 3, color: '#6b7280' }}>
                    No users with chats found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <React.Fragment key={user.user_id}>
                  <TableRow
                    sx={{
                      '&:hover': {
                        backgroundColor: '#f8f9fb',
                      },
                      cursor: 'pointer',
                    }}
                    onClick={() => handleExpandUser(user.user_id)}
                  >
                    <TableCell sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>
                      <Box>
                        <Typography variant="body2" fontWeight="bold" sx={{ color: '#374151' }}>
                          {user.username}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                          {user.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>
                      <Chip
                        label={user.session_count}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb',
                          bgcolor: '#f8f9fb',
                          color: '#374151',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>
                      <Chip
                        label={user.total_messages}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb',
                          bgcolor: '#f8f9fb',
                          color: '#374151',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>
                      <Typography variant="body2" sx={{ color: '#374151' }}>
                        {formatRelativeTime(user.last_chat_time)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>
                      <IconButton size="small">
                        {expandedUser === user.user_id ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded Chat Details */}
                  <TableRow>
                    <TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
                      <Collapse in={expandedUser === user.user_id} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: '#f8f9fb' }}>
                          {loadingChats ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                              <CircularProgress size={24} sx={{ color: '#9ca3af' }} />
                            </Box>
                          ) : userChats.length === 0 ? (
                            <Typography variant="body2" align="center" sx={{ py: 2, color: '#6b7280' }}>
                              No chat sessions found
                            </Typography>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {userChats.map((session) => (
                                <Card
                                  key={session.session_id}
                                  variant="outlined"
                                  sx={{
                                    border: '1px solid #eceef1',
                                    borderRadius: '10px',
                                    boxShadow: 'none',
                                  }}
                                >
                                  <CardContent>
                                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                                      <Box>
                                        <Typography component="h3" sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827' }}>
                                          {session.session_name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                          Created: {formatDate(session.created_at)}
                                        </Typography>
                                      </Box>
                                      <Chip
                                        label={`${session.message_count} messages`}
                                        size="small"
                                        icon={<Message />}
                                        sx={{
                                          height: 22,
                                          fontSize: '0.7rem',
                                          borderRadius: '6px',
                                          border: '1px solid #e5e7eb',
                                          bgcolor: '#f8f9fb',
                                          color: '#374151',
                                        }}
                                      />
                                    </Box>
                                    
                                    {session.first_message && (
                                      <Box sx={{ mb: 1 }}>
                                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                          First:
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                        >
                                          {session.first_message.message}
                                        </Typography>
                                      </Box>
                                    )}
                                    
                                    {session.last_message && (
                                      <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                          Last:
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                        >
                                          {session.last_message.message}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                          ({formatRelativeTime(session.last_message.timestamp)})
                                        </Typography>
                                      </Box>
                                    )}
                                    
                                    {session.recent_messages.length > 0 && (
                                      <Box>
                                        <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>
                                          Recent Messages:
                                        </Typography>
                                        <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: '#eceef1' }}>
                                          {session.recent_messages.map((msg, idx) => (
                                            <Box key={idx} sx={{ mb: 1 }}>
                                              <Chip
                                                label={msg.sender}
                                                size="small"
                                                sx={{
                                                  mr: 1,
                                                  mb: 0.5,
                                                  height: 20,
                                                  fontSize: '0.68rem',
                                                  borderRadius: '6px',
                                                  ...(msg.sender === 'user'
                                                    ? { bgcolor: '#111827', color: '#fff' }
                                                    : { bgcolor: '#f3f4f6', color: '#374151' }),
                                                }}
                                              />
                                              <Typography
                                                variant="body2"
                                                component="span"
                                                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                              >
                                                {msg.message}
                                              </Typography>
                                              <Typography variant="caption" sx={{ ml: 1, color: '#6b7280' }}>
                                                {formatRelativeTime(msg.timestamp)}
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Box>
                                      </Box>
                                    )}
                                  </CardContent>
                                </Card>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, '& .MuiPagination-root': { '& .MuiPaginationItem-root': { fontSize: '0.82rem' } } }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => fetchUsers(value)}
            color="primary"
            size="small"
          />
        </Box>
      )}
    </Box>
  );
};

export default UserChats;

